import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { getTechniciansSearch } from 'queries';
import { Toast, parseErrors } from 'helpers';
import type { Technician, JsonDataItem, Arg0, Condition } from 'types';

type TechnicianSearchParam = Arg0<typeof getTechniciansSearch>['searchParam'];

export type TechnicianSearchParams = {
  searchParam: TechnicianSearchParam;
  searchValue: string;
  status?: Condition | '';
  page?: number;
  perPage?: number;
};

async function queryFn({
  queryKey,
}: QueryFunctionContext<[string, TechnicianSearchParams]>): Promise<{ technicians: Technician[]; meta: any }> {
  const [_, params] = queryKey;

  try {
    const response = await getTechniciansSearch(params);
    return { technicians: response.data, meta: response.meta };
  } catch (err) {
    Toast.show({ description: parseErrors(err as JsonDataItem).join(', '), type: Toast.Type.Error });
    return { technicians: [], meta: { total: 0 } };
  }
}

type TechniciansWithMetadata = {
  technicians?: Technician[];
  total?: number;
  isLoading: boolean;
};

export function useTechnicianSearch(params: TechnicianSearchParams): TechniciansWithMetadata {
  const { data, isInitialLoading: isLoading } = useQuery({
    queryFn,
    queryKey: ['technician-search', params] as [string, TechnicianSearchParams],
  });

  return {
    technicians: data?.technicians,
    total: data?.meta?.total,
    isLoading,
  };
}
